import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal"];
  static values = {
    id: String,
  };

  connect() {
    // Listen for Turbo's element morphing event to handle modal content updates
    document.addEventListener(
      "turbo:before-morph-element",
      this.handleBeforeMorphElement
    );

    document.addEventListener("keyup", this.closeOnEscapeKey);

    // Optional, because the backdrop is not guaranteed to
    // be there. PhlexyUI's `Modal#body` emits it only under
    // the `:tap_outside_to_close` modifier, and
    // `handleBeforeMorphElement` below replaces this
    // dialog's whole innerHTML from whatever element a
    // re-render hands it. Every modal in the app renders one
    // today, so this is defensive: what makes it worth
    // guarding is that a throw here compounds, since
    // `reconnect_controllers` re-runs both halves of this
    // lifecycle on EVERY morph and reports what it catches,
    // and each report costs a session replay.
    this.element
      .querySelector(".modal-backdrop")
      ?.addEventListener("click", this.close);

    if (!this.idValue) {
      throw new Error(
        "Modal ID is required to know which modal to open"
      );
    }

    // When inside #window-component (admin preview),
    // use show() instead of showModal() so the dialog
    // stays in the normal DOM flow. showModal() promotes
    // to the browser's top layer, escaping contain:layout
    // and breaking @container queries.
    this.contained = !!this.element.closest(
      "#window-component"
    );

    // When a Turbo Frame replaces the modal content
    // (e.g., after a 422 validation error re-render),
    // the new dialog element has the modal-open class but
    // was never opened. Re-establish the modal state.
    //
    // Only for a dialog that is actually in the page. A
    // morph can run connect() against an element it has
    // already detached, and both show() and showModal()
    // throw InvalidStateError on an element that is in no
    // document. There is nothing to re-establish for one
    // either: the connect() on the attached element does
    // the job.
    if (
      this.element.isConnected &&
      this.element.classList.contains("modal-open") &&
      !this.element.open
    ) {
      if (this.contained) {
        this.element.show();
      } else {
        this.element.showModal();
      }
    }
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-morph-element",
      this.handleBeforeMorphElement
    );

    document.removeEventListener("keyup", this.closeOnEscapeKey);

    this.element
      .querySelector(".modal-backdrop")
      ?.removeEventListener("click", this.close);
  }

  // This handler prevents Turbo from removing the modal during page morphs
  // while still allowing its content to update. Without this, the modal would
  // disappear and leave a stuck overlay when the page updates.
  //
  // We only handle the event when:
  // 1. The modal is open (this.element.open)
  // 2. The event targets our modal (event.target === this.element)
  //
  // Then we:
  // 1. Prevent Turbo's default morphing behavior
  // 2. Manually update the modal's content while preserving its open state
  handleBeforeMorphElement = (event) => {
    if (this.element.open && event.target === this.element) {
      event.preventDefault();
      const { newElement } = event.detail;

      if (newElement) {
        this.element.innerHTML = newElement.innerHTML;
      } else {
        // This could happen when deleting a menu item. Since the delete button
        // is inside the modal, when morphed, the newElement will be null.
        this.element.close();
      }
    }
  };

  // `requestModalOpen` is dispatched on the WINDOW, so
  // every connected controller is handed every request
  // whatever state its own dialog is in, and whoever
  // opened it. Only a dialog that is shut and in the page
  // can be opened, exactly as in `connect()`:
  //
  // - A morph can leave this controller listening on an
  //   element it has already detached, and `showModal()`
  //   refuses one that is in no document. `show()` does
  //   not refuse, which is worse: it marks a dialog open
  //   that nobody can see.
  // - A dialog that is already open is refused when the
  //   request disagrees with the browser about modality,
  //   and these dialogs are opened both ways
  //   (`Turbo.StreamActions.open_modal` opens without a
  //   backdrop unless the stream says otherwise). Already
  //   open is already what the request wanted, so there
  //   is nothing to do for one either.
  open({ detail: { id, displayBackdrop } }) {
    if (this.idValue !== id) return;
    if (!this.element.isConnected || this.element.open) {
      return;
    }

    if (this.contained) {
      this.element.show();
    } else if (displayBackdrop) {
      this.element.showModal();
    } else {
      this.element.show();
    }
  }

  dispatchClosed() {
    this.dispatch("modalClosed", {
      detail: { id: this.idValue },
      prefix: false,
    });
  }

  closeOnEscapeKey = (event) => {
    if (event.key === "Escape") {
      this.close();
    }
  };

  close = () => {
    this.element.classList.remove("modal-open");
    this.element.close();
    this.dispatchClosed();
  };
}
